'use client';

import React, { useMemo } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { InvariantChecker } from 'src/utils/UIInvariants/InvariantChecker';
import { cj } from 'src/utils/classNames';
import ToggleTabs from 'src/components/primitives/ToggleTabs/ToggleTabs';
import { ToggleTab } from 'src/components/primitives/ToggleTabs/types';
import { MarketType } from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';
import {
	STANDARD_BUTTON_HORIZONTAL_PADDING_CLASSNAME,
	STANDARD_BUTTON_TEXT_SIZE_CLASSNAME,
	STANDARD_BUTTON_VERTICAL_PADDING_CLASSNAME,
} from 'src/components/primitives/Button/consts';

type Side = 'buy' | 'sell';

interface Props {
	onChange: (newSide: Side) => void;
}

const BuySellToggle = ({ onChange }: Props) => {
	const isOnBuySide = useDriftStore((s) => s.tradeForm.side === 'buy');
	const marketType = useDriftStore((s) => s.selectedMarket.current?.marketType);
	const isPerp = ENUM_UTILS.match(marketType, MarketType.PERP);
	const isLessThanXL = useWindowSizeIsLessThan('xl');
	const size = isLessThanXL ? 'sm' : 'md';

	const tabs: ToggleTab<Side>[] = useMemo(() => {
		return [
			{
				label: `${isPerp ? 'Long' : 'Buy'}`,
				value: 'buy',
			},
			{
				label: `${isPerp ? 'Short' : 'Sell'}`,
				value: 'sell',
			},
		];
	}, [isPerp]);

	return (
		<div
			{...InvariantChecker.getPositionDirectionInputHtmlAttributes(isOnBuySide)}
		>
			<ToggleTabs
				options={tabs}
				activeTab={isOnBuySide ? 'buy' : 'sell'}
				onTabChange={onChange}
				variant="longShort"
				tabClassName={cj(
					STANDARD_BUTTON_HORIZONTAL_PADDING_CLASSNAME[size],
					STANDARD_BUTTON_VERTICAL_PADDING_CLASSNAME[size],
					STANDARD_BUTTON_TEXT_SIZE_CLASSNAME[size]
				)}
				dataTestId="trade-direction-toggle"
				tabTestIdPrefix="trade-direction"
			/>
		</div>
	);
};

export default React.memo(BuySellToggle);
